import { useEffect, useState } from "react"

import { useDispatch, useSelector } from "react-redux"

import { useDemoDataToggle } from "../../../User/hooks/useDemoDataToggle"
import {
  getDescriptorsByEnrollment,
  getMoreDescriptorsByEnrollment
} from "../../../User/redux/user.thunks"

const FETCH_DEBOUNCE = 300

const useFetchDescriptorsList = ({
  enrollmentId,
  filters,
  PAGE_SIZE,
  statusesReady,
  userEnrollmentsFetching
}) => {
  const dispatch = useDispatch()
  const {
    descriptorsByEnrollment,
    descriptorsByEnrollmentFetching,
    moreDescriptorsByEnrollmentFetching
  } = useSelector((state) => state.user)
  const [page, setPage] = useState(1)
  const [fetchScheduled, setFetchScheduled] = useState(false)
  const hasMore = descriptorsByEnrollment?.count > page * PAGE_SIZE
  const moreLoading = moreDescriptorsByEnrollmentFetching
  const loading =
    descriptorsByEnrollmentFetching || fetchScheduled || !statusesReady || userEnrollmentsFetching
  const descriptors = descriptorsByEnrollment?.results || []
  const { isShowDemoData } = useDemoDataToggle()

  const buildParams = (nextPage) => ({
    enrollment_id: enrollmentId,
    descriptor: filters?.search || null,
    status: filters?.statuses?.join(",") || null,
    page: nextPage,
    page_size: PAGE_SIZE
  })

  const fetchMore = () => {
    if (!enrollmentId || !hasMore || moreLoading) {
      return
    }
    dispatch(getMoreDescriptorsByEnrollment(buildParams(page + 1)))
    setPage((prevPage) => prevPage + 1)
  }

  // Single entry point for the first page: waits for the available statuses so the request
  // always carries the filter the dropdown shows, and debounces so an enrollment switch or
  // a search keystroke fires one request instead of several racing ones.
  useEffect(() => {
    if (!enrollmentId || userEnrollmentsFetching || !statusesReady) {
      setFetchScheduled(false)
      return
    }
    setFetchScheduled(true)
    const timeout = setTimeout(() => {
      setFetchScheduled(false)
      dispatch(getDescriptorsByEnrollment(buildParams(1)))
      setPage(1)
    }, FETCH_DEBOUNCE)
    return () => clearTimeout(timeout)
  }, [enrollmentId, isShowDemoData, userEnrollmentsFetching, statusesReady, filters])

  return { descriptors, fetchMore, hasMore, loading, moreLoading }
}

export default useFetchDescriptorsList
